import { useEffect, useState } from 'react';
import { Loader2, Download, Copy as CopyIcon, ExternalLink } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

/**
 * QR dialog — fetches the catalog QR PNG via authenticated api call and
 * shows it with copy-link + download-png actions. The QR always encodes the
 * production URL (server-side), so the same PNG works for print + digital.
 */
export default function CatalogQRDialog({ catalog, open, onOpenChange }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const publicUrl = catalog
    ? `https://ijehaus.com/c/${catalog.slug}`
    : '';

  useEffect(() => {
    if (!open || !catalog) return;
    let revoked = false;
    let created = null;
    (async () => {
      setLoading(true);
      setBlobUrl(null);
      try {
        const resp = await api.get(`/catalogs/${catalog.id}/qr.png?size=720`, { responseType: 'blob' });
        created = URL.createObjectURL(resp.data);
        if (!revoked) setBlobUrl(created);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not generate QR');
      } finally { setLoading(false); }
    })();
    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [open, catalog]);

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `ijehaus-${catalog.slug}-qr.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  if (!catalog) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="catalog-qr-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-2xl">QR code</DialogTitle>
          <p className="sans text-xs tracking-widest uppercase text-neutral-500">{catalog.name}</p>
        </DialogHeader>

        <div className="py-2 space-y-4">
          <div className="border border-neutral-200 p-4 bg-white flex items-center justify-center min-h-[280px]">
            {loading || !blobUrl ? (
              <Loader2 size={28} className="animate-spin text-neutral-400" />
            ) : (
              <img
                src={blobUrl}
                alt="Catalog QR code"
                className="w-64 h-64 object-contain"
                data-testid="catalog-qr-image"
              />
            )}
          </div>

          <div>
            <p className="sans text-[10px] tracking-widest uppercase text-neutral-500 mb-1">Scans to</p>
            <p className="sans text-xs text-neutral-700 font-mono break-all" data-testid="catalog-qr-url">{publicUrl}</p>
          </div>

          <p className="sans text-xs text-neutral-500 leading-relaxed">
            Print this on flyers, line-sheets, or share the image directly.
            Anyone who scans it lands on the catalog and is invited to sign in
            or apply for account-level pricing.
          </p>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={copy}
              className="btn-outline h-10 justify-center"
              data-testid="catalog-qr-copy"
            >
              <CopyIcon size={14} /> Copy link
            </button>
            <button
              type="button"
              onClick={download}
              disabled={!blobUrl}
              className="btn-primary h-10 justify-center"
              data-testid="catalog-qr-download"
            >
              <Download size={14} /> Download PNG
            </button>
          </div>

          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="sans text-[11px] tracking-widest uppercase text-neutral-500 hover:text-black inline-flex items-center gap-1"
          >
            <ExternalLink size={12} /> Open live catalog
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
